<template>
    <g-base-box class="products-item">
        <g-row type="flex" justify="space-around" align="middle">
            <g-column :span="5">
                <div style="height: 66px; margin-right: 15px">
                    <g-image-thumbnail
                        :image-link="product.image"
                        :image-description="product.name"
                        image-units-string="%"
                        :background-blur="false"
                    />
                </div>
            </g-column>
            <g-column :span="19">
                <g-typography tag="h4" font-weight="semibold" :value="product.name" />
                <g-typography v-if="quantity > 1">x{{ quantity }}</g-typography>
                <g-typography v-if="showPrices" tag="h4" :value="productPrice" color="secondary" />
                <div v-if="outOfStock"><g-tag color="red"> Out of Stock </g-tag></div>
            </g-column>
        </g-row>
        <g-level v-if="needsSelections">
            <g-level-item>
                <g-button type="link" @click="$emit('edit')"> Select Options </g-button>
            </g-level-item>
        </g-level>
        <template v-else-if="isBriefEditable || optionsStr || isAdditionalOrder">
            <g-divider />
            <g-level>
                <g-level-item v-if="optionsStr">
                    <g-typography color="info" size="small" v-html="$sanitize(optionsStr)" />
                </g-level-item>
                <g-level-item v-if="isBriefEditable || isAdditionalOrder" slot="right">
                    <div class="margin-right-small">
                        <g-button
                            v-if="hasConfigurableOptions"
                            size="small"
                            :minimalist="true"
                            icon="pencil"
                            @click="$emit('edit')"
                        >
                            Edit
                        </g-button>
                    </div>
                    <g-button
                        v-if="!selectionMethodPreselected"
                        size="small"
                        :minimalist="true"
                        icon="trash-alt"
                        @click="$emit('remove')"
                    >
                        Remove
                    </g-button>
                </g-level-item>
            </g-level>
        </template>
    </g-base-box>
</template>

<script>
    import { get } from 'lodash';
    import ActivationBriefProductConfigMixin from '../../mixins/ActivationBriefProductConfigMixin';
    import ActivationBriefMixin from '../../mixins/ActivationBriefMixin';
    import { formatCurrency } from '../../../../../helpers/formatters';

    export default {
        name: 'ActivationBriefProductsItem',
        props: {
            product: {
                type: Object,
                required: true
            },
            options: {
                type: Array,
                required: true
            },
            isAdditionalOrder: {
                type: Boolean,
                default: false
            },
            hasExistingOrder: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                editVisibility: true,
                removeVisibility: true
            };
        },
        mixins: [ActivationBriefMixin, ActivationBriefProductConfigMixin],
        computed: {
            hasOptions() {
                return !!this.product.options.length;
            },
            needsSelections() {
                return this.options.length < this.product.options.length;
            },
            optionsMapped() {
                // Map to consistent format
                return this.options.map((o) => ({
                    id: o.option_id || o.id,
                    name: o.label || o.name,
                    value_id: o.value || o.value_id
                }));
            },
            optionsStr() {
                if (this.hasOptions) {
                    return this.optionsMapped.map((option) => option.name).join(', ');
                }

                return '';
            },
            configItem() {
                return this.getProductConfigItem('product_id', this.product.id);
            },
            hasConfigurableOptions() {
                return (
                    (this.hasOptions && !this.selectionMethodPreselected) ||
                    (this.selectionMethodPreselected &&
                        get(this, 'configItem.product_option_values', []).filter((option) => option.value === -1)
                            .length > 0)
                );
            },
            quantity() {
                return this.hasExistingOrder ? 1 : this.configItem.quantity || 1;
            },
            productVariant() {
                return this.getProductVariantFromOptionValues(this.optionsMapped);
            },
            outOfStock() {
                return this.productVariant ? !this.isVariantInStock : !this.product.is_in_stock;
            },
            productPrice() {
                return formatCurrency(
                    this.productVariant ? this.productVariant.price : this.product.price,
                    get(this, 'brief.brand.currency', 'usd')
                );
            },
            isVariantInStock() {
                return get(this.productVariant, 'is_in_stock', false);
            }
        },
        methods: {
            getProductVariantFromOptionValues(optionValues) {
                const variants = get(this.product, 'variants', []);

                return variants.find((variant) => {
                    return variant.option_values.every((variantOptionValue) => {
                        const optionValue = optionValues.find((val) => val.id === variantOptionValue.option_id);
                        return optionValue && optionValue.value_id === variantOptionValue.id;
                    });
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .products-item {
        margin-bottom: 20px;
    }
    .margin-right-small {
        margin-right: $g-spacing-2;
    }
</style>
